// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — a live stream's first and newest frame and its cadence, read fresh
 * from the stream host for the Situation room's data ages (slice CR2). The
 * page's own timeline cache keeps what it read at load, which a wall
 * running for hours must not trust: here a stream is asked again after five
 * minutes, bypassing the HTTP cache.
 *
 * A value-encoded release row follows its `latest.json` to the
 * `release.json` (start, frame count, cadence); any other stream reads its
 * `.dsa`. Frames are placed on the stream's own calendar (`frameCalendar.ts`).
 */

import type { Dataset } from '../types'
import { createFrameAxis, lastFrameTimeMs } from './frameCalendar'
import { lastFrameDateMs, parseDsaTimeline } from './dsaTimeline'

export interface StreamDates {
  firstFrameMs: number
  lastFrameMs: number
  cadenceMs: number
}

const TTL_MS = 5 * 60_000
const cache = new Map<string, { at: number; dates: Promise<StreamDates | null> }>()

const rec = (v: unknown): Record<string, unknown> | null => (typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : null)
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)

async function getJson(url: string, fetchImpl: typeof fetch): Promise<Record<string, unknown> | null> {
  const res = await fetchImpl(url, { headers: { Accept: 'application/json' }, cache: 'no-cache' })
  return res.ok ? rec(await res.json()) : null
}

/** A release.json's dates. */
export function releaseDates(release: unknown): StreamDates | null {
  const body = rec(release)
  const range = rec(body?.timeRange)
  const startMs = typeof range?.start === 'string' ? Date.parse(range.start) : NaN
  const frames = num(body?.timeTotalFrames)
  const cadenceS = num(body?.timeCadenceSeconds)
  if (!Number.isFinite(startMs) || frames === null || frames < 1 || cadenceS === null || cadenceS <= 0) return null
  const cadenceMs = cadenceS * 1000
  return { firstFrameMs: startMs, lastFrameMs: lastFrameTimeMs(createFrameAxis({ startMs, frameCount: frames, cadenceMs })), cadenceMs }
}

async function read(ds: Dataset, fetchImpl: typeof fetch): Promise<StreamDates | null> {
  try {
    if (ds.releaseDescriptorLink) {
      const latest = await getJson(ds.releaseDescriptorLink, fetchImpl)
      const pointer = rec(latest?.releaseDescriptor)?.url
      if (typeof pointer !== 'string' || !pointer) return null
      return releaseDates(await getJson(new URL(pointer, ds.releaseDescriptorLink).toString(), fetchImpl))
    }
    if (ds.timelineLink) {
      const timeline = parseDsaTimeline(await getJson(ds.timelineLink, fetchImpl))
      if (!timeline) return null
      return { firstFrameMs: timeline.startMs, lastFrameMs: lastFrameDateMs(timeline), cadenceMs: timeline.cadenceMs }
    }
  } catch {
    /* unreachable now: unknown, asked again later */
  }
  return null
}

/** The stream's dates, from the cache while they are under five minutes old. */
export function streamDates(ds: Dataset, fetchImpl: typeof fetch = fetch, now = Date.now()): Promise<StreamDates | null> {
  const held = cache.get(ds.id)
  if (held && now - held.at < TTL_MS) return held.dates
  const dates = read(ds, fetchImpl)
  cache.set(ds.id, { at: now, dates })
  return dates
}

/** What the cache holds now, without asking (null: not asked yet, still on its way, or unknown). */
export function cachedStreamDates(id: string): Promise<StreamDates | null> | null {
  return cache.get(id)?.dates ?? null
}

/** Tests only. */
export function clearStreamDatesForTests(): void {
  cache.clear()
}
